"use client";

import { SelectField, TextField } from "@/components/tool-kit/primitives";
import { HINT, LABEL } from "@/components/tool-kit/styles";
import type { CLASSROOM_NOISE_METER_STRINGS } from "@/components/tools/classroom-noise-meter";
import { useToolLocale, useToolNumber } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  MIN_GAP,
  SENSITIVITY_RANGE,
  type Settings,
  withThreshold,
} from "@/lib/tool-kit/games-organizers/noise-meter";

type Strings = typeof CLASSROOM_NOISE_METER_STRINGS;

export const WARN_CHOICES = [0, 2, 3, 5, 10, 15, 30] as const;

function Slider({
  id,
  label,
  value,
  min,
  max,
  hint,
  onChange,
}: {
  id: string;
  label: string;
  value: number;
  min: number;
  max: number;
  hint?: string;
  onChange: (value: number) => void;
}) {
  return (
    <div className="space-y-2">
      <label className={LABEL} htmlFor={id}>
        {label}
      </label>
      <input
        className="w-full accent-primary"
        id={id}
        max={max}
        min={min}
        onChange={(event) => onChange(Number(event.target.value))}
        type="range"
        value={value}
      />
      {hint ? <p className={HINT}>{hint}</p> : null}
    </div>
  );
}

/** Sensitivity, the two thresholds and the warning. */
export default function NoiseSettings({
  settings,
  warnAfter,
  message,
  strings,
  onSettings,
  onWarnAfter,
  onMessage,
}: {
  settings: Settings;
  warnAfter: number;
  message: string;
  strings: Strings;
  onSettings: (next: Settings) => void;
  onWarnAfter: (seconds: number) => void;
  onMessage: (text: string) => void;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const sign = settings.sensitivity > 0 ? "+" : "";
  return (
    <div className="grid gap-5 sm:grid-cols-2">
      <Slider
        hint={strings.sensitivityHint}
        id="noise-sensitivity"
        label={t(strings.sensitivityLabel, {
          value: `${sign}${fmt(settings.sensitivity)}`,
        })}
        max={SENSITIVITY_RANGE}
        min={-SENSITIVITY_RANGE}
        onChange={(sensitivity) => onSettings({ ...settings, sensitivity })}
        value={settings.sensitivity}
      />
      <div className="hidden sm:block" />
      <Slider
        hint={strings.quietHint}
        id="noise-quiet"
        label={t(strings.quietLabel, { value: fmt(settings.quietBelow) })}
        max={100 - MIN_GAP}
        min={MIN_GAP}
        onChange={(value) =>
          onSettings(withThreshold(settings, "quietBelow", value))
        }
        value={settings.quietBelow}
      />
      <Slider
        hint={strings.loudHint}
        id="noise-loud"
        label={t(strings.loudLabel, { value: fmt(settings.loudAbove) })}
        max={100}
        min={2 * MIN_GAP}
        onChange={(value) =>
          onSettings(withThreshold(settings, "loudAbove", value))
        }
        value={settings.loudAbove}
      />
      <SelectField
        hint={strings.warnHint}
        id="noise-warn"
        label={strings.warnLabel}
        onChange={(value) => onWarnAfter(Number(value))}
        options={WARN_CHOICES.map((n) => ({
          value: String(n),
          label:
            n === 0
              ? strings.warnOff
              : tPlural(strings.warnOption, locale, n, { count: fmt(n) }),
        }))}
        value={String(warnAfter)}
      />
      <TextField
        id="noise-message"
        label={strings.messageLabel}
        onChange={onMessage}
        placeholder={strings.messagePlaceholder}
        value={message}
      />
    </div>
  );
}
